import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/Dialog';
import ErrorMessage from '@/components/ErrorMessage';
import ScrollArea from '@/components/ScrollArea';
import VisuallyHidden from '@/components/VisuallyHidden';
import Input from '@/components/form/Input';
import FormTextArea from '@/components/form/TextArea';
import transactionModel from '@/models/transaction';
import { cn, showSuccess } from '@/utils';

function ShareSendByEmail({ defaultFileName, id, onOpenChange, open }) {
	const [errorMessage, setErrorMessage] = useState('');
	const { t } = useTranslation();

	const [loading, setLoading] = useState(false);

	const INPUTS = {
		EMAIL: 'email',
		FILE_NAME: 'fileName',
		MESSAGE: 'message'
	};

	const validationSchema = Yup.object({
		[INPUTS.EMAIL]: Yup.string().email().required(),
		[INPUTS.FILE_NAME]: Yup.string().max(100),
		[INPUTS.MESSAGE]: Yup.string().max(500)
	});

	const methods = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			[INPUTS.EMAIL]: '',
			[INPUTS.FILE_NAME]: defaultFileName || '',
			[INPUTS.MESSAGE]: ''
		}
	});

	async function onSubmit(formValues) {
		const { email, fileName, message } = formValues;
		setErrorMessage('');
		setLoading(true);

		try {
			await transactionModel.sendByEmail(id, email, fileName, message);

			showSuccess(t('editor.shareModal.sendByEmail.successToast'));
			onOpenChange(false);
		} catch (error) {
			setErrorMessage(t('errors.generic'));
		}
		setLoading(false);
	}

	useEffect(() => {
		if (!open) {
			methods.reset();
			setErrorMessage('');
		}
	}, [open]);

	return (
		<FormProvider {...methods}>
			<form
				onSubmit={methods.handleSubmit(onSubmit)}
				className="flex w-full flex-col items-center justify-center rounded-lg bg-white"
			>
				<DialogHeader className="flex h-12 w-full flex-row items-center justify-start border-b border-grey-200 px-4">
					<DialogTitle className="font-title text-xs font-semibold leading-6 md:text-sm md:leading-6">
						{t('editor.shareModal.sendByEmail.title')}
					</DialogTitle>
					<VisuallyHidden asChild>
						<DialogDescription>
							{t('editor.shareModal.sendByEmail.description')}
						</DialogDescription>
					</VisuallyHidden>
				</DialogHeader>

				<ScrollArea
					className="max-h-[calc(100vh-8rem)] w-full"
					maxHeight="100%"
				>
					<div className="flex max-h-[70vh] w-full flex-col gap-y-4 p-4 lg:pt-6">
						<Input
							label={t('editor.shareModal.sendByEmail.labelFileName')}
							name={INPUTS.FILE_NAME}
							placeholder={`${t('editor.shareModal.sendByEmail.placeholderFileName')}`}
							size="lg"
						/>
						<Input
							label={t('editor.shareModal.sendByEmail.labelEmail')}
							name={INPUTS.EMAIL}
							placeholder={`${t('editor.shareModal.sendByEmail.placeholderEmail')}`}
							size="lg"
						/>
						<FormTextArea
							className="min-h-[100px]"
							name={INPUTS.MESSAGE}
							size="lg"
							label={t('editor.shareModal.sendByEmail.labelMessage')}
							placeholder={`${t('editor.shareModal.sendByEmail.placeholderMessage')}`}
						/>
					</div>

					<ErrorMessage
						className={cn(
							'mt-0 px-4 text-center',
							!!errorMessage && 'pb-2'
						)}
					>
						{errorMessage}
					</ErrorMessage>
				</ScrollArea>

				<DialogFooter className="w-full">
					<Button
						className="w-full"
						onClick={() => onOpenChange(false)}
						variant="outline"
					>
						{t('common.cancel')}
					</Button>
					<Button className="w-full" loading={loading} type="submit">
						{t('common.send')}
					</Button>
				</DialogFooter>
			</form>
		</FormProvider>
	);
}

export default ShareSendByEmail;
